/**
 * The one definition of "which users match" for the all-orgs Users screen, shared by the list
 * read (`listAdminUsers`) and the matching-user count (`countMatchingUsers`).
 *
 * The two used to spell this out separately, which meant the toolbar's count and the rows under
 * it agreed only by hand: adding a searchable column or changing the pending rule in one file
 * would have made the number quietly disagree with the list, with nothing failing.
 *
 * Search is expressed as a list of filters, one query each, rather than a single `.or()` string:
 * user input must never reach an `.or()` filter, where a comma or parenthesis would malform the
 * clause. Each caller applies a filter to its own query builder (they select different columns
 * and only the list carries a keyset cursor), so what lives here is the contract: which columns
 * are searched, how the term is escaped, and when a term also counts as a `user_id`.
 */

import { escapeIlike } from "@/lib/utils/escape-ilike";

import { ADMIN_USERS_MAX_SEARCH_LENGTH, ADMIN_USERS_MIN_SEARCH_LENGTH } from "./constants";

/** Digits-only terms of up to nine digits are treated as a `user_id` too; nine digits always fits
 *  the `integer` column, where a longer number would make Postgres reject the whole query. */
const USER_ID_SEARCH_PATTERN = /^\d{1,9}$/;

/** One branch of the search: a substring match on a text column, or an exact `user_id` match. */
export type UserSearchFilter =
  | { kind: "ilike"; column: "email" | "full_name"; pattern: string }
  | { kind: "eq"; column: "user_id"; value: number };

/**
 * Normalizes a raw search box value into the term the reads actually use.
 *
 * @param search - Raw input, possibly undefined.
 * @returns The trimmed, length-capped term, or "" when it is too short to search on (there is no
 *   `pg_trgm` index yet, so a 1-character `ilike` would seq-scan for nothing).
 */
export function normalizeUserSearch(search: string | undefined): string {
  const trimmed = (search ?? "").trim().slice(0, ADMIN_USERS_MAX_SEARCH_LENGTH);
  return trimmed.length >= ADMIN_USERS_MIN_SEARCH_LENGTH ? trimmed : "";
}

/**
 * The filters a search fans out into, one query per filter, merged by the caller.
 *
 * @param search - A term already through {@link normalizeUserSearch}.
 * @returns One filter per searched column, or an empty array for an empty term (no search).
 */
export function buildUserSearchFilters(search: string): UserSearchFilter[] {
  if (search === "") return [];

  const pattern = `%${escapeIlike(search)}%`;
  const filters: UserSearchFilter[] = [
    { kind: "ilike", column: "email", pattern },
    { kind: "ilike", column: "full_name", pattern },
  ];
  if (USER_ID_SEARCH_PATTERN.test(search)) {
    filters.push({ kind: "eq", column: "user_id", value: Number(search) });
  }
  return filters;
}

/**
 * The slice of a PostgREST builder these helpers use. Deliberately structural and self-returning
 * rather than generic over Supabase's own builder type: chaining two generic helpers over that
 * type is enough nesting for tsc to bail out with "type instantiation is excessively deep".
 */
interface UsersQueryBuilder {
  ilike: (column: string, pattern: string) => UsersQueryBuilder;
  eq: (column: string, value: number) => UsersQueryBuilder;
  in: (column: string, values: number[]) => UsersQueryBuilder;
  is: (column: string, value: null) => UsersQueryBuilder;
  not: (column: string, operator: string, value: null) => UsersQueryBuilder;
}

/**
 * Applies one search filter to a query builder.
 *
 * @param query - Any PostgREST builder for `nowadays.users`.
 * @param filter - One filter from {@link buildUserSearchFilters}.
 * @returns The filtered builder, as the caller's own builder type.
 */
export function applyUserSearchFilter<T>(query: T, filter: UserSearchFilter): T {
  const builder = query as UsersQueryBuilder;
  const filtered =
    filter.kind === "ilike" ? builder.ilike(filter.column, filter.pattern) : builder.eq(filter.column, filter.value);
  return filtered as T;
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------
//
// Both filters reach `nowadays.users` through an EMBED on the membership table rather than a
// second query, which is what keeps them compatible with the list's keyset paging: PostgREST
// filters *top-level* rows through an embed, so `LIMIT` still counts users and the `user_id`
// ordering is untouched. Verified against production: an org filter on an org with 11 members
// across 23 membership rows reports 11, not 23, so the exact count stays exact.
//
// The `select` string has to carry the embed for these to apply, which is why the shape lives
// here next to them (`userSelectWithFilters`) instead of being spelled out at each call site.

/** Which slice of the membership relationship a list is asking for. */
export type AdminUsersMembership = "any" | "in_group" | "no_group";

/** The `enterprise_groups_members` embed each membership mode needs on the `select` string. */
const MEMBERSHIP_EMBED: Record<AdminUsersMembership, string> = {
  // No embed at all: an unfiltered list must not pay for a join.
  any: "",
  // `!inner` drops users with no membership; the org filter narrows through the same embed.
  in_group: ", enterprise_groups_members!inner(enterprise_groups!inner(organization_id))",
  // `!left` keeps everyone, and the `is null` filter below then keeps only the unmatched rows.
  no_group: ", enterprise_groups_members!left(user_id)",
};

/**
 * The `select` string for a users read, with whatever embed its filters need appended.
 *
 * @param columns - The `nowadays.users` columns the caller wants.
 * @param filters - The list's filter values.
 * @returns A PostgREST select string.
 */
export function userSelectWithFilters(columns: string, filters: AdminUsersFilters): string {
  // An org filter implies the inner embed even on "any": you cannot filter through a join that
  // isn't there.
  const mode: AdminUsersMembership = filters.orgIds.length > 0 ? "in_group" : filters.membership;
  return `${columns}${MEMBERSHIP_EMBED[mode]}`;
}

/** The list's filter values, as the toolbar holds them. */
export interface AdminUsersFilters {
  /** `enterprise_organizations.id`s; empty means every org. */
  orgIds: number[];
  membership: AdminUsersMembership;
}

/** No filters applied. */
export const EMPTY_ADMIN_USERS_FILTERS: AdminUsersFilters = { orgIds: [], membership: "any" };

/**
 * Applies the org and membership filters to a query built with {@link userSelectWithFilters}.
 *
 * `no_group` is an anti-join, which PostgREST expresses as "the left-joined embed came back
 * null". `in_group` needs no predicate at all: its `!inner` embed has already dropped everyone
 * without a membership.
 *
 * @param query - A PostgREST builder whose select carries the matching embed.
 * @param filters - The list's filter values.
 * @returns The filtered builder, as the caller's own builder type.
 */
export function applyUserFilters<T>(query: T, filters: AdminUsersFilters): T {
  const builder = query as UsersQueryBuilder;

  if (filters.orgIds.length > 0) {
    return builder.in("enterprise_groups_members.enterprise_groups.organization_id", filters.orgIds) as T;
  }
  if (filters.membership === "no_group") {
    return builder.is("enterprise_groups_members", null) as T;
  }
  return query;
}

// ---------------------------------------------------------------------------
// Sort
// ---------------------------------------------------------------------------
//
// Deliberately limited to columns that can carry the keyset cursor on their own. `user_id` is the
// primary key and `email` is UNIQUE and indexed, so each is a complete cursor by itself. Name and
// last-active are NOT offered: neither is unique (so they would need a composite cursor, which
// PostgREST can only express through an `.or()` string this module exists to avoid) and neither is
// indexed, so sorting on them would full-scan every search sub-query on every keystroke.

/** How the list is ordered. The cursor column follows from this. */
export type AdminUsersSort = "newest" | "oldest" | "email";

/** The ordering one sort asks for, and the row field its cursor is read from. */
interface SortDescriptor {
  column: "user_id" | "email";
  ascending: boolean;
}

const SORT_DESCRIPTORS: Record<AdminUsersSort, SortDescriptor> = {
  newest: { column: "user_id", ascending: false },
  oldest: { column: "user_id", ascending: true },
  email: { column: "email", ascending: true },
};

/**
 * The ordering a sort asks for.
 *
 * @param sort - The list's sort.
 * @returns Its column and direction.
 */
export function sortDescriptor(sort: AdminUsersSort): SortDescriptor {
  return SORT_DESCRIPTORS[sort];
}

/** A keyset cursor: the sort column's value on the last row of the previous page. */
export type AdminUsersCursor = number | string;

/**
 * Validates a cursor against the sort it claims to belong to. The cursor is client-supplied input
 * to a server action, so its shape is checked rather than trusted.
 *
 * @param cursor - The cursor as received.
 * @param sort - The sort the request also carries.
 * @returns True when the cursor can be used with that sort.
 */
export function isValidCursor(cursor: AdminUsersCursor, sort: AdminUsersSort): boolean {
  const { column } = sortDescriptor(sort);
  if (column === "user_id") return typeof cursor === "number" && Number.isSafeInteger(cursor);
  return typeof cursor === "string" && cursor.length > 0 && cursor.length <= ADMIN_USERS_MAX_SEARCH_LENGTH;
}

/**
 * Reads the cursor value for the next page off the last row of this one.
 *
 * @param row - The last row emitted.
 * @param sort - The list's sort.
 * @returns The value the next page's cursor predicate compares against.
 */
export function cursorFromRow(row: { user_id: number; email: string | null }, sort: AdminUsersSort): AdminUsersCursor {
  return sortDescriptor(sort).column === "user_id" ? row.user_id : (row.email ?? "");
}

/**
 * Orders two rows the way the database did, for merging the search's parallel sub-queries.
 *
 * @param sort - The list's sort.
 * @returns A comparator matching that sort.
 */
export function sortComparator(
  sort: AdminUsersSort
): (a: { user_id: number; email: string | null }, b: { user_id: number; email: string | null }) => number {
  const { column, ascending } = sortDescriptor(sort);
  const direction = ascending ? 1 : -1;
  if (column === "user_id") return (a, b) => (a.user_id - b.user_id) * direction;
  return (a, b) => (a.email ?? "").localeCompare(b.email ?? "") * direction;
}
